import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, CalendarDays, DollarSign } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";
import { SectionLabel } from "@/components/shared";
import { AGE_GROUPS, SESSION_INFO } from "@/data/site";

function PageHero() {
  return (
    <section className="relative overflow-hidden pb-16 pt-40 md:pt-48" data-testid="training-hero">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionLabel>Programs</SectionLabel>
        <h1 className="font-heading text-6xl uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[8rem]">
          <MaskLines lines={["Training", <span className="text-neon-green">Built For You</span>]} />
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/70 md:text-xl">
            Three age-specific programs, one relentless standard. Find your player's level and register below.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function SessionBanner() {
  return (
    <section className="relative z-10 bg-obsidian pb-6" data-testid="training-session-banner">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="border border-neon-green/40 bg-zinc-900 p-6 sm:p-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 font-body text-xs font-bold uppercase tracking-[0.3em] text-neon-green">
                  <CalendarDays size={16} strokeWidth={2.5} /> Now Enrolling · {SESSION_INFO.name}
                </div>
                <h2 className="mt-3 font-heading text-3xl uppercase leading-none text-white sm:text-5xl">{SESSION_INFO.start}</h2>
                <p className="mt-3 font-body text-sm text-white/70">{SESSION_INFO.cadence} · All age groups (7–9, 10–12, 13–15)</p>
              </div>
              <div className="border border-neon-green/30 bg-obsidian px-5 py-3">
                <div className="flex items-center gap-2 font-heading text-xl uppercase text-neon-green">
                  <DollarSign size={18} strokeWidth={2.5} /> {SESSION_INFO.price}
                </div>
                <p className="mt-1 font-body text-[10px] uppercase tracking-wider text-white/40">+ $5 online fee</p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {SESSION_INFO.dates.map((d) => (
                <span key={d} className="border border-white/15 bg-obsidian px-3 py-1.5 font-body text-xs font-bold uppercase tracking-wide text-white/80">
                  {d}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function GroupSection({ g, index }) {
  const flip = index % 2 === 1;
  return (
    <section id={g.id} data-testid={`training-section-${g.id}`} className="relative z-10 scroll-mt-24 border-t border-white/10 bg-obsidian py-20 md:py-28">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="overflow-hidden border border-white/10">
              <img src={g.image} alt={g.title} className="aspect-[4/3] w-full object-cover grayscale transition-all duration-700 hover:grayscale-0" />
            </div>
          </Reveal>
          <div>
            <div className="flex items-baseline gap-4">
              <span className="font-heading text-7xl text-stroke md:text-8xl">{g.id}</span>
              <span className="font-body text-xs font-bold uppercase tracking-[0.3em] text-neon-green">{g.tag}</span>
            </div>
            <h2 className="mt-4 font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-5xl">{g.title}</h2>
            <p className="mt-4 max-w-lg font-body text-base leading-relaxed text-white/70">{g.blurb}</p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {g.focus.map((f) => (
                <li key={f} className="flex items-center gap-3 font-body text-sm text-white/80">
                  <Check size={18} strokeWidth={3} className="shrink-0 text-neon-green" /> {f}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-6 border-t border-white/10 pt-6">
              <div className="flex items-center gap-2 font-body text-sm text-white/50">
                <CalendarDays size={16} strokeWidth={2.5} className="text-neon-green" /> {g.schedule}
              </div>
              <div className="flex items-center gap-2 font-body text-sm font-bold text-neon-green" data-testid={`training-price-${g.id}`}>
                <DollarSign size={16} strokeWidth={2.5} /> {g.price}
              </div>
            </div>

            <Link
              to={`/register?group=${g.id}`}
              data-testid={`training-register-${g.id}`}
              className="group mt-8 inline-flex items-center gap-2 bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black"
            >
              Register For {g.id}
              <ArrowUpRight size={18} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Training() {
  return (
    <div data-testid="training-page">
      <PageHero />
      <SessionBanner />
      {AGE_GROUPS.map((g, i) => (
        <GroupSection key={g.id} g={g} index={i} />
      ))}
      <section className="relative z-10 bg-neon-green py-20 text-center md:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-black sm:text-6xl">Not Sure Which Fits?</h2>
          <p className="mt-4 font-body text-base font-medium text-black/70">
            Register your player and our staff will help place them in the right group.
          </p>
          <Link to="/register" data-testid="training-general-register" className="mt-8 inline-flex items-center gap-2 border-2 border-black bg-black px-10 py-5 font-heading text-lg uppercase tracking-widest text-neon-green hover:bg-transparent hover:text-black">
            Sign Up For Training
          </Link>
        </div>
      </section>
    </div>
  );
}
